import { supabase } from '../supabase';
import type { ScheduleConfig } from '../types';

interface ScheduleReportParams {
  report_id: string;
  schedule_config: ScheduleConfig;
}

interface ScheduleResult {
  success: boolean;
  message: string;
  next_execution?: string;
  schedule_config?: ScheduleConfig;
}

export class ReportSchedulingService {
  // Schedule a report for automatic execution
  static async scheduleReport(
    reportId: string,
    scheduleConfig: ScheduleConfig
  ): Promise<ScheduleResult> {
    try {
      // Call the Supabase Edge Function for report scheduling
      const { data, error } = await supabase.functions.invoke('schedule-report', {
        body: {
          report_id: reportId,
          schedule_config: scheduleConfig
        }
      });

      if (error) {
        throw new Error(error.message || 'Scheduling failed');
      }

      return {
        success: data.success || false,
        message: data.message || 'Report scheduled',
        next_execution: data.next_execution,
        schedule_config: data.schedule_config
      };

    } catch (error) {
      console.error('Report scheduling error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to schedule report'
      };
    }
  }

  // Disable report scheduling
  static async disableSchedule(reportId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('custom_reports')
        .update({
          is_scheduled: false,
          schedule_config: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', reportId);

      return !error;
    } catch (error) {
      console.error('Error disabling schedule:', error);
      return false;
    }
  }

  // Get scheduled reports
  static async getScheduledReports(organizationId?: string): Promise<any[]> {
    try {
      let query = supabase
        .from('custom_reports')
        .select(`
          id,
          name,
          description,
          schedule_config,
          is_scheduled,
          updated_at,
          executions:report_executions(
            id,
            status,
            scheduled_for,
            executed_at
          )
        `)
        .eq('is_scheduled', true)
        .order('updated_at', { ascending: false });

      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error getting scheduled reports:', error);
      return [];
    }
  }

  // Get next scheduled executions
  static async getUpcomingExecutions(
    hours: number = 24,
    organizationId?: string
  ): Promise<any[]> {
    try {
      const now = new Date();
      const future = new Date(now.getTime() + hours * 60 * 60 * 1000);

      let query = supabase
        .from('report_executions')
        .select(`
          *,
          reports:custom_reports(
            name,
            description,
            schedule_config
          )
        `)
        .eq('execution_type', 'scheduled')
        .eq('status', 'pending')
        .gte('scheduled_for', now.toISOString())
        .lte('scheduled_for', future.toISOString())
        .order('scheduled_for', { ascending: true });

      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error getting upcoming executions:', error);
      return [];
    }
  }

  // Update schedule configuration
  static async updateSchedule(
    reportId: string,
    scheduleConfig: Partial<ScheduleConfig>
  ): Promise<boolean> {
    try {
      // Get current schedule config
      const { data: report } = await supabase
        .from('custom_reports')
        .select('schedule_config')
        .eq('id', reportId)
        .single();

      const currentConfig = report?.schedule_config || {};
      const newConfig = { ...currentConfig, ...scheduleConfig };

      const { error } = await supabase
        .from('custom_reports')
        .update({
          schedule_config: newConfig,
          updated_at: new Date().toISOString()
        })
        .eq('id', reportId);

      return !error;
    } catch (error) {
      console.error('Error updating schedule:', error);
      return false;
    }
  }

  // Calculate next execution time
  static calculateNextExecution(
    frequency: string,
    time?: string,
    timezone: string = 'Europe/Rome'
  ): Date {
    const now = new Date();
    const nextExecution = new Date(now);
    
    // Parse time if provided (format: "HH:MM")
    let hours = 9; // Default to 9 AM
    let minutes = 0;
    
    if (time) {
      const [h, m] = time.split(':').map(Number);
      if (!isNaN(h) && !isNaN(m)) {
        hours = h;
        minutes = m;
      }
    }
    
    switch (frequency) {
      case 'hourly':
        nextExecution.setHours(nextExecution.getHours() + 1);
        nextExecution.setMinutes(0);
        nextExecution.setSeconds(0);
        break;
        
      case 'daily':
        nextExecution.setDate(nextExecution.getDate() + 1);
        nextExecution.setHours(hours);
        nextExecution.setMinutes(minutes);
        nextExecution.setSeconds(0);
        break;
        
      case 'weekly':
        nextExecution.setDate(nextExecution.getDate() + 7);
        nextExecution.setHours(hours);
        nextExecution.setMinutes(minutes);
        nextExecution.setSeconds(0);
        break;
        
      case 'monthly':
        nextExecution.setMonth(nextExecution.getMonth() + 1);
        nextExecution.setHours(hours);
        nextExecution.setMinutes(minutes);
        nextExecution.setSeconds(0);
        break;
        
      default:
        // Default to daily
        nextExecution.setDate(nextExecution.getDate() + 1);
        nextExecution.setHours(hours);
        nextExecution.setMinutes(minutes);
        nextExecution.setSeconds(0);
    }
    
    return nextExecution;
  }

  // Validate schedule configuration
  static validateScheduleConfig(config: ScheduleConfig): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Validate frequency
    const validFrequencies = ['hourly', 'daily', 'weekly', 'monthly'];
    if (!validFrequencies.includes(config.frequency)) {
      errors.push('Invalid frequency. Must be hourly, daily, weekly, or monthly.');
    }

    // Validate time format
    if (config.time && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(config.time)) {
      errors.push('Invalid time format. Use HH:MM format (e.g., 09:30).');
    }

    // Validate email recipients
    if (config.email_recipients && config.email_recipients.length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = config.email_recipients.filter(email => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        errors.push(`Invalid email addresses: ${invalidEmails.join(', ')}`);
      }
    }

    // Validate export formats
    if (config.export_formats && config.export_formats.length > 0) {
      const validFormats = ['pdf', 'excel', 'csv'];
      const invalidFormats = config.export_formats.filter(format => !validFormats.includes(format));
      if (invalidFormats.length > 0) {
        errors.push(`Invalid export formats: ${invalidFormats.join(', ')}`);
      }
    }

    // Validate timezone
    if (config.timezone) {
      try {
        Intl.DateTimeFormat(undefined, { timeZone: config.timezone });
      } catch {
        errors.push('Invalid timezone.');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // Get schedule status summary
  static async getScheduleSummary(organizationId?: string): Promise<{
    total_scheduled: number;
    active_schedules: number;
    pending_executions: number;
    recent_executions: number;
  }> {
    try {
      // Get total scheduled reports
      let totalQuery = supabase
        .from('custom_reports')
        .select('id', { count: 'exact', head: true })
        .eq('is_scheduled', true);

      if (organizationId) {
        totalQuery = totalQuery.eq('organization_id', organizationId);
      }

      const { count: totalScheduled } = await totalQuery;

      // Get pending executions (next 24 hours)
      const now = new Date();
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      
      let pendingQuery = supabase
        .from('report_executions')
        .select('id', { count: 'exact', head: true })
        .eq('execution_type', 'scheduled')
        .eq('status', 'pending')
        .gte('scheduled_for', now.toISOString())
        .lte('scheduled_for', tomorrow.toISOString());

      if (organizationId) {
        pendingQuery = pendingQuery.eq('organization_id', organizationId);
      }

      const { count: pendingExecutions } = await pendingQuery;

      // Get recent executions (last 24 hours)
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      let recentQuery = supabase
        .from('report_executions')
        .select('id', { count: 'exact', head: true })
        .eq('execution_type', 'scheduled')
        .gte('executed_at', yesterday.toISOString());

      if (organizationId) {
        recentQuery = recentQuery.eq('organization_id', organizationId);
      }

      const { count: recentExecutions } = await recentQuery;

      return {
        total_scheduled: totalScheduled || 0,
        active_schedules: totalScheduled || 0, // Simplified - in production, check if schedules are actually active
        pending_executions: pendingExecutions || 0,
        recent_executions: recentExecutions || 0
      };

    } catch (error) {
      console.error('Error getting schedule summary:', error);
      return {
        total_scheduled: 0,
        active_schedules: 0,
        pending_executions: 0,
        recent_executions: 0
      };
    }
  }

  // Test schedule configuration (dry run)
  static async testSchedule(config: ScheduleConfig): Promise<{
    next_5_executions: string[];
    validation: { valid: boolean; errors: string[] };
    estimated_frequency: string;
  }> {
    const validation = this.validateScheduleConfig(config);
    
    if (!validation.valid) {
      return {
        next_5_executions: [],
        validation,
        estimated_frequency: 'Invalid configuration'
      };
    }

    // Calculate next 5 execution times
    const executions: string[] = [];
    let nextTime = this.calculateNextExecution(config.frequency, config.time, config.timezone);
    
    for (let i = 0; i < 5; i++) {
      executions.push(nextTime.toISOString());
      
      // Calculate next execution after this one
      switch (config.frequency) {
        case 'hourly':
          nextTime = new Date(nextTime.getTime() + 60 * 60 * 1000);
          break;
        case 'daily':
          nextTime = new Date(nextTime.getTime() + 24 * 60 * 60 * 1000);
          break;
        case 'weekly':
          nextTime = new Date(nextTime.getTime() + 7 * 24 * 60 * 60 * 1000);
          break;
        case 'monthly':
          nextTime = new Date(nextTime.getFullYear(), nextTime.getMonth() + 1, nextTime.getDate(), nextTime.getHours(), nextTime.getMinutes());
          break;
      }
    }

    // Estimate frequency in human-readable format
    const frequencyMap = {
      hourly: 'Every hour',
      daily: 'Every day',
      weekly: 'Every week',
      monthly: 'Every month'
    };

    const estimatedFrequency = `${frequencyMap[config.frequency as keyof typeof frequencyMap]}${config.time ? ` at ${config.time}` : ''}`;

    return {
      next_5_executions: executions,
      validation,
      estimated_frequency: estimatedFrequency
    };
  }
}